import { supabase } from "@/integrations/supabase/client";
import i18n from "@/i18n";

/**
 * Accès au bucket `invoices`.
 *
 * Pourquoi ce module : `storage.from('invoices').list('')` s'arrête à 100
 * entrées (limite par défaut de `@supabase/storage-js`). Le bucket étant plat,
 * la numérotation des factures repartait de la centième facture connue et
 * réattribuait des numéros déjà utilisés — et l'aperçu, ne retrouvant pas la
 * facture existante, régénérait un second PDF pour le même abonnement.
 * Tout passage par le bucket doit donc utiliser ces helpers, jamais `list('')`.
 */

/**
 * ---------------------------------------------------------------------------
 * Conventions de nommage des fichiers du bucket `invoices`
 * ---------------------------------------------------------------------------
 *
 * ANCIENNE convention (héritée, encore présente dans le bucket) :
 *
 *     <8 premiers caractères de l'identifiant de transaction>_<YYYY-NNN>.pdf
 *     ex. « sub-1a2b_2025-014.pdf »
 *
 *   Défaut : les lignes de paiement portent un identifiant de la forme
 *   `sub-<uuid>`. Les 8 premiers caractères valent donc « sub- » suivi de
 *   4 caractères hexadécimaux seulement, soit 65 536 valeurs possibles.
 *   Au-delà de quelques centaines d'abonnements facturés, deux transactions
 *   finissent par partager le même préfixe (≈ 23 % de risque à 200 factures,
 *   ≈ 69 % à 400) : l'aperçu de la seconde retrouvait le PDF de la première,
 *   se verrouillait et refusait d'émettre la facture manquante.
 *
 * NOUVELLE convention (celle qu'on écrit désormais) :
 *
 *     tx_<identifiant de transaction COMPLET, assaini>__<YYYY-NNN>.pdf
 *     ex. « tx_sub-1a2b3c4d-5e6f-7081-9234-56789abcdef0__2026-042.pdf »
 *
 *   - le préfixe « tx_ » et le double souligné « __ » séparant l'identifiant
 *     du numéro rendent la génération du nom reconnaissable sans ambiguïté ;
 *   - l'identifiant est repris ENTIER : plus aucune troncature, donc plus
 *     aucune collision entre deux factures ÉMISES SOUS CETTE CONVENTION ;
 *   - l'identifiant est assaini (cf. `sanitizeTransactionKey`) pour ne
 *     contenir que des caractères sûrs dans une clé d'objet Supabase Storage :
 *     pas de « / » (qui créerait un dossier), pas d'espace ni de caractère
 *     accentué. Les identifiants réels (`sub-<uuid>` ou un uuid nu) ne
 *     contiennent que `[0-9a-f-]` et traversent l'assainissement inchangés.
 *
 * COMPATIBILITÉ ASCENDANTE : aucun fichier existant n'est renommé. La
 * recherche (`findInvoiceForTransaction`) tente d'abord la nouvelle convention
 * — comparaison d'égalité stricte sur l'identifiant complet — puis retombe sur
 * l'ancienne — comparaison d'égalité stricte sur les 8 premiers caractères.
 * Les deux comparaisons sont des égalités sur un nom entièrement analysé, et
 * non un `startsWith` : un fichier de la nouvelle convention ne peut donc pas
 * être capté par le repli hérité, et réciproquement.
 *
 * LIMITE RÉSIDUELLE, à connaître avant d'annoncer le défaut comme clos : le
 * repli hérité compare toujours 8 caractères, dont 4 seulement sont
 * discriminants. Tant qu'une transaction B n'a pas SA PROPRE facture en
 * nouvelle convention, elle continue donc de se voir attribuer la facture
 * héritée d'une transaction A partageant le préfixe — et `uploadInvoice`
 * répond `alreadyExisted: true` sans rien émettre. Le nommage complet supprime
 * la collision pour toute facture émise à partir de maintenant, il ne peut pas
 * réparer les rapprochements ambigus du stock existant. Seul un renommage du
 * bucket (à partir d'une table de correspondance facture → transaction, qui
 * n'existe pas côté application) lèverait ce reliquat.
 *
 * NUMÉROTATION : inchangée. Le numéro visible par le client reste `YYYY-NNN`
 * et la séquence reste continue, car `extractInvoiceNumber` lit le suffixe
 * `_<YYYY>-<NNN>.pdf` commun aux deux conventions.
 */

export interface InvoiceFile {
  name: string;
  [key: string]: unknown;
}

/** Préfixe marquant un nom écrit selon la convention actuelle. */
const CURRENT_PREFIX = "tx_";

/** `tx_<clé>__<année>-<numéro>.pdf` (le `.+` gourmand cale sur le DERNIER `__`). */
const CURRENT_NAME_RE = /^tx_(.+)__(\d{4})-(\d{3,})\.pdf$/;

/** `<préfixe 8 car.>_<année>-<numéro>.pdf` (le `-?` tolère un identifiant tronqué sur un tiret). */
const LEGACY_NAME_RE = /^([^_]+)_-?(\d{4})-(\d{3,})\.pdf$/;

/** Longueur du préfixe historiquement retenu comme clé de rapprochement. */
const LEGACY_KEY_LENGTH = 8;

/**
 * Caractères conservés tels quels dans une clé d'objet Supabase Storage.
 * Volontairement plus restrictif que ce que le service accepte : on exclut en
 * particulier « / » (séparateur de dossier) et l'espace.
 */
const UNSAFE_KEY_CHARS = /[^A-Za-z0-9._-]/g;

/** Rend un identifiant de transaction utilisable dans un nom d'objet storage. */
export function sanitizeTransactionKey(transactionId: string): string {
  const cleaned = String(transactionId).trim().replace(UNSAFE_KEY_CHARS, "-");
  // Un nom d'objet vide, « . » ou « .. » serait refusé par le storage.
  return cleaned.length > 0 && cleaned !== "." && cleaned !== ".." ? cleaned : "sans-identifiant";
}

/** Clé de rapprochement de l'ancienne convention (préfixe tronqué). */
function legacyTransactionKey(transactionId: string): string {
  return String(transactionId).substring(0, LEGACY_KEY_LENGTH);
}

export type InvoiceNamingConvention = "current" | "legacy";

export interface ParsedInvoiceName {
  /** Convention détectée. */
  convention: InvoiceNamingConvention;
  /** Identifiant complet (nouvelle convention) ou préfixe tronqué (ancienne). */
  transactionKey: string;
  year: number;
  /** Numéro de séquence, sans le zéro de tête. */
  sequence: number;
  /** Numéro tel qu'imprimé sur la facture, `YYYY-NNN`. */
  invoiceNumber: string;
}

/**
 * Analyse un nom de fichier du bucket selon l'une des deux conventions.
 * Renvoie `null` pour tout nom parasite (fichier déposé à la main, export,
 * numéro sur moins de 3 chiffres, extension autre que `.pdf`, …).
 */
export function parseInvoiceFileName(fileName: string): ParsedInvoiceName | null {
  const build = (
    convention: InvoiceNamingConvention,
    key: string,
    yearRaw: string,
    sequenceRaw: string
  ): ParsedInvoiceName => ({
    convention,
    transactionKey: key,
    year: Number.parseInt(yearRaw, 10),
    sequence: Number.parseInt(sequenceRaw, 10),
    // On réutilise la chaîne d'origine : un numéro à 4 chiffres reste à 4.
    invoiceNumber: `${yearRaw}-${sequenceRaw}`,
  });

  if (fileName.startsWith(CURRENT_PREFIX)) {
    const m = CURRENT_NAME_RE.exec(fileName);
    if (m) return build("current", m[1], m[2], m[3]);
    // Nom commençant par « tx_ » mais mal formé : on ne le fait pas passer
    // pour un nom hérité, ce serait un rapprochement au hasard.
    return null;
  }

  const legacy = LEGACY_NAME_RE.exec(fileName);
  if (legacy) return build("legacy", legacy[1], legacy[2], legacy[3]);

  return null;
}

/** Nom de fichier à écrire pour une transaction (convention actuelle). */
export function buildInvoiceFileName(transactionId: string, invoiceNumber: string): string {
  return `${CURRENT_PREFIX}${sanitizeTransactionKey(transactionId)}__${invoiceNumber}.pdf`;
}

const PAGE_SIZE = 1000;
// Garde-fou : au-delà on préfère s'arrêter plutôt que boucler indéfiniment si
// l'API renvoyait toujours une page pleine.
const MAX_PAGES = 100;

/**
 * Liste l'intégralité du bucket, page par page.
 * Lève en cas d'erreur : un appelant qui numéroterait sur une liste tronquée
 * réattribuerait un numéro existant, il vaut mieux échouer visiblement.
 */
export async function listAllInvoices(prefix = ""): Promise<InvoiceFile[]> {
  const all: InvoiceFile[] = [];

  for (let page = 0; page < MAX_PAGES; page++) {
    const { data, error } = await supabase.storage
      .from("invoices")
      .list(prefix, { limit: PAGE_SIZE, offset: page * PAGE_SIZE });

    if (error) throw error;

    // FileObject de storage-js n'a pas d'index signature : le passage par
    // unknown est nécessaire ; la seule propriété lue est le nom du fichier.
    const batch = (data || []) as unknown as InvoiceFile[];
    all.push(...batch);

    if (batch.length < PAGE_SIZE) break;
  }

  return all;
}

/** Variante tolérante : renvoie [] et journalise, pour les usages d'affichage. */
export async function listAllInvoicesSafe(prefix = ""): Promise<InvoiceFile[]> {
  try {
    return await listAllInvoices(prefix);
  } catch (error) {
    console.error("[invoicesStorage] Impossible de lister le bucket invoices:", error);
    return [];
  }
}

/**
 * Numéro de facture (`YYYY-NNN`) contenu dans un nom de fichier, sinon null.
 *
 * Volontairement tolérant sur ce qui précède le numéro : il ne lit que le
 * suffixe `_<année>-<numéro>.pdf`, commun aux deux conventions. C'est ce qui
 * alimente la numérotation ; une lecture plus stricte ignorerait d'éventuels
 * noms hérités atypiques et ferait réattribuer un numéro déjà émis.
 */
export function extractInvoiceNumber(fileName: string, year: number): number | null {
  const match = fileName.match(new RegExp(`_-?${year}-(\\d{3,})\\.pdf$`));
  if (!match) return null;
  const parsed = Number.parseInt(match[1], 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Calcule le prochain numéro à partir d'une liste **complète**.
 * Pur, pour rester testable et pour forcer l'appelant à fournir la liste
 * entière plutôt qu'une page.
 */
export function computeNextInvoiceNumber(files: InvoiceFile[], year = new Date().getFullYear()): string {
  const numbers = files
    .map((f) => extractInvoiceNumber(f.name, year))
    .filter((n): n is number => n !== null);

  const max = numbers.length > 0 ? Math.max(...numbers) : 0;
  return `${year}-${String(max + 1).padStart(3, "0")}`;
}

/** Prochain numéro disponible, calculé sur le bucket entier. */
export async function getNextInvoiceNumber(): Promise<string> {
  const files = await listAllInvoices();
  return computeNextInvoiceNumber(files);
}

/**
 * Retrouve, dans une liste déjà chargée, la facture émise pour une transaction.
 *
 * Ordre de recherche :
 *   1. nouvelle convention — égalité stricte sur l'identifiant COMPLET ;
 *   2. repli ancienne convention — égalité stricte sur les 8 premiers
 *      caractères, pour ne pas rendre orphelines les factures déjà émises.
 *
 * Si les deux existent pour la même transaction (facture héritée puis nouvelle
 * émission), la nouvelle l'emporte : c'est celle dont l'identifiant est certain.
 */
export function findInvoiceForTransactionIn(
  transactionId: string,
  files: InvoiceFile[]
): InvoiceFile | null {
  const id = String(transactionId);
  const currentKey = sanitizeTransactionKey(id);
  const legacyKey = legacyTransactionKey(id);

  const parsed = files.map((file) => ({ file, parsed: parseInvoiceFileName(file.name) }));

  const current = parsed.find(
    (e) => e.parsed?.convention === "current" && e.parsed.transactionKey === currentKey
  );
  if (current) return current.file;

  const legacyMatches = parsed.filter(
    (e) => e.parsed?.convention === "legacy" && e.parsed.transactionKey === legacyKey
  );

  if (legacyMatches.length > 1) {
    // Collision héritée : deux transactions distinctes partagent le préfixe
    // tronqué. On ne peut pas trancher sans renommer le bucket ; on signale et
    // on garde le comportement historique (la première trouvée).
    console.warn(
      `[invoicesStorage] ${legacyMatches.length} factures héritées partagent le préfixe "${legacyKey}" : ` +
        `rapprochement ambigu pour la transaction ${id}. ` +
        `Fichiers : ${legacyMatches.map((e) => e.file.name).join(", ")}`
    );
  }

  return legacyMatches[0]?.file ?? null;
}

/** Retrouve la facture déjà émise pour une transaction, sur le bucket entier. */
export async function findInvoiceForTransaction(
  transactionId: string,
  files?: InvoiceFile[]
): Promise<InvoiceFile | null> {
  const list = files ?? (await listAllInvoices());
  return findInvoiceForTransactionIn(transactionId, list);
}

/* ---------------------------------------------------------------------------
 * Cloisonnement par salle
 * ---------------------------------------------------------------------------
 *
 * Historiquement toutes les salles déposaient leurs factures à la RACINE du
 * bucket et la numérotation était calculée sur ce dossier commun : la première
 * facture de Maison Pilates a donc reçu 2026-007, six numéros ayant déjà été
 * consommés par Vision Body.
 *
 * Désormais chaque salle écrit dans son propre dossier `<gym_id>/` et la
 * séquence `YYYY-NNN` est calculée UNIQUEMENT sur ce dossier.
 *
 * La racine reste le dossier de Vision Body – Salle Principale : les factures
 * déjà émises y restent en place (aucun déplacement d'objet, donc aucun lien
 * cassé) et la série de cette salle se poursuit sans trou.
 *
 * Les factures émises à la racine par une AUTRE salle avant ce changement
 * restent retrouvables : la recherche par transaction retombe sur la racine.
 * Elles ne sont simplement plus comptées dans la numérotation de cette salle.
 */

/** Salle dont les factures vivent à la racine du bucket (historique). */
export const LEGACY_ROOT_GYM_ID = "20994998-a4fd-463f-be28-45036c7c505a";

/** Dossier du bucket où écrire les factures d'une salle ("" = racine). */
export function invoiceFolderForGym(gymId?: string | null): string {
  if (!gymId || gymId === LEGACY_ROOT_GYM_ID) return "";
  return gymId;
}

/** Chemin complet d'un objet du bucket pour une salle. */
export function invoiceObjectPath(gymId: string | null | undefined, fileName: string): string {
  const folder = invoiceFolderForGym(gymId);
  return folder ? `${folder}/${fileName}` : fileName;
}

export interface GymInvoiceIndex {
  gymId: string | null;
  /** Dossier de la salle ("" pour la salle historique, à la racine). */
  folder: string;
  /** Factures de la salle. */
  files: InvoiceFile[];
  /** Factures héritées restées à la racine (vide si la salle EST la racine). */
  legacyFiles: InvoiceFile[];
}

export interface LocatedInvoice {
  file: InvoiceFile;
  /** Chemin à utiliser pour signer / télécharger / remplacer l'objet. */
  path: string;
  /** La facture est restée à la racine, hors du dossier de la salle. */
  isLegacyRoot: boolean;
}

const EMPTY_INDEX = (gymId: string | null): GymInvoiceIndex => ({
  gymId,
  folder: invoiceFolderForGym(gymId),
  files: [],
  legacyFiles: [],
});

/** Charge les factures de la salle (et le reliquat racine si besoin). */
export async function loadGymInvoiceIndex(gymId: string | null | undefined): Promise<GymInvoiceIndex> {
  const folder = invoiceFolderForGym(gymId);
  const files = await listAllInvoices(folder);
  // Inutile de relire la racine quand la salle EST la racine : ce serait la
  // même liste, et le double comptage fausserait la numérotation.
  const legacyFiles = folder ? await listAllInvoices("") : [];
  return { gymId: gymId ?? null, folder, files, legacyFiles };
}

/** Variante tolérante, pour les usages d'affichage. */
export async function loadGymInvoiceIndexSafe(
  gymId: string | null | undefined
): Promise<GymInvoiceIndex> {
  try {
    return await loadGymInvoiceIndex(gymId);
  } catch (error) {
    console.error("[invoicesStorage] Lecture des factures de la salle impossible:", error);
    return EMPTY_INDEX(gymId ?? null);
  }
}

/** Prochain numéro de la salle : calculé sur son seul dossier. */
export function computeNextInvoiceNumberForGym(
  index: GymInvoiceIndex,
  year = new Date().getFullYear()
): string {
  return computeNextInvoiceNumber(index.files, year);
}

/** Retrouve la facture d'une transaction : dossier de la salle, puis racine. */
export function findInvoiceInIndex(
  index: GymInvoiceIndex,
  transactionId: string
): LocatedInvoice | null {
  const own = findInvoiceForTransactionIn(transactionId, index.files);
  if (own) {
    return {
      file: own,
      path: index.folder ? `${index.folder}/${own.name}` : own.name,
      isLegacyRoot: index.folder === "",
    };
  }

  const legacy = findInvoiceForTransactionIn(transactionId, index.legacyFiles);
  if (legacy) {
    return { file: legacy, path: legacy.name, isLegacyRoot: true };
  }

  return null;
}

export interface UploadedInvoice {
  fileName: string;
  /** Chemin complet dans le bucket (dossier de la salle inclus). */
  path: string;
  invoiceNumber: string;
  /** La facture existait déjà : rien n'a été envoyé, on a repris l'existante. */
  alreadyExisted: boolean;
}

/**
 * Envoie le PDF sous un numéro qui n'a pas encore été attribué.
 *
 * Le numéro est (re)calculé juste avant l'envoi, et `upsert: false` sert de
 * garde-fou : si un autre poste a pris le numéro entre-temps, on recalcule et
 * on réessaie au lieu d'écraser ou de dupliquer.
 */
export async function uploadInvoice(
  gymId: string | null | undefined,
  transactionId: string,
  buildPdf: (invoiceNumber: string) => Promise<Blob>,
  maxAttempts = 3
): Promise<UploadedInvoice> {
  let lastError: unknown = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const index = await loadGymInvoiceIndex(gymId);

    // Une facture existe déjà pour cette transaction (dossier de la salle ou
    // reliquat racine, nouvelle convention ou héritée) : on ne réémet pas.
    const existing = findInvoiceInIndex(index, transactionId);
    if (existing) {
      // Le numéro est relu dans le nom du fichier, y compris l'année : une
      // facture émise l'an dernier doit rester affichée avec SON année.
      const parsed = parseInvoiceFileName(existing.file.name);
      return {
        fileName: existing.file.name,
        path: existing.path,
        invoiceNumber: parsed?.invoiceNumber ?? "",
        alreadyExisted: true,
      };
    }

    const invoiceNumber = computeNextInvoiceNumberForGym(index);
    const fileName = buildInvoiceFileName(transactionId, invoiceNumber);
    const path = invoiceObjectPath(gymId, fileName);
    const pdfBlob = await buildPdf(invoiceNumber);

    const { error } = await supabase.storage.from("invoices").upload(path, pdfBlob, {
      cacheControl: "3600",
      upsert: false,
      contentType: "application/pdf",
    });

    if (!error) return { fileName, path, invoiceNumber, alreadyExisted: false };

    lastError = error;
    console.warn(
      `[invoicesStorage] Envoi de ${fileName} refusé (tentative ${attempt + 1}/${maxAttempts}):`,
      error
    );
  }

  throw lastError ?? new Error(i18n.t("invoices.sendFailed", { ns: "appui" }));
}

/**
 * Remplace le PDF d'une facture DÉJÀ émise, à numéro et chemin inchangés.
 *
 * Réservé à la correction d'une facture : le numéro imprimé ne doit pas bouger,
 * sinon la série comporterait un trou et deux PDF porteraient le même numéro.
 * L'écriture est autorisée par la politique storage aux seuls administrateurs
 * de la salle propriétaire du dossier.
 */
export async function replaceInvoice(path: string, pdfBlob: Blob): Promise<void> {
  const { error } = await supabase.storage.from("invoices").upload(path, pdfBlob, {
    cacheControl: "3600",
    upsert: true,
    contentType: "application/pdf",
  });

  if (error) throw error;
}
